/**
 * useCampaignDeepLinkTab — makes the campaign-detail active tab driven by the
 * typed `?tab` search param.
 *
 * Design: the URL is the single source of truth. The Zustand store's
 * `activeTab` is mirrored ONE-WAY (URL → store) so other store readers stay in
 * sync, but it is never written back to the URL — that avoids the render loop a
 * two-way sync would create.
 *
 * With no `?tab`, the caller's `landingTab` is written to the URL (replacing
 * the history entry): other hooks read `?tab` directly, and a pinned choice
 * can't be moved by a later campaign refetch.
 */
import { useCallback, useEffect } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useCampaignUiStore } from '@discovr/features';
import {
  DISCOVERY_TAB,
  LEGACY_ACCESS_TAB,
  legacySettingsSub,
  clearedShopifyListParams,
  withoutDiscoveryParams,
  type CampaignSearch,
  type CampaignTab,
} from './search';

const leavingDiscovery = (prev: Partial<CampaignSearch>, next: string): Partial<CampaignSearch> =>
  prev.tab === DISCOVERY_TAB && next !== DISCOVERY_TAB ? withoutDiscoveryParams() : {};

/**
 * @param landingTab Tab to open when the URL names none; `null` while that
 * can't be decided yet, in which case the returned `tab` is `null` too.
 */
export function useCampaignDeepLinkTab(landingTab: CampaignTab | null) {
  const search = useSearch({ strict: false }) as Partial<CampaignSearch>;
  const navigate = useNavigate();
  // Legacy links are redirected by the page; their tab is not a tab here. The
  // explicit access-control check narrows `tab` to a real CampaignTab.
  const urlTab = search.tab === LEGACY_ACCESS_TAB || legacySettingsSub(search) !== null ? undefined : search.tab;
  const tab = urlTab ?? landingTab;
  const setStoreTab = useCampaignUiStore((s) => s.setActiveTab);

  // One-way URL → store. The store mirror keeps other store readers in sync.
  useEffect(() => {
    if (tab === null) return;
    setStoreTab(tab);
  }, [tab, setStoreTab]);

  const shouldWriteLandingTab = urlTab === undefined && landingTab !== null;
  useEffect(() => {
    if (!shouldWriteLandingTab) return;
    void navigate({
      to: '.',
      replace: true,
      search: ((prev: Partial<CampaignSearch>) => ({ ...prev, tab: landingTab })),
    } as never);
  }, [shouldWriteLandingTab, landingTab, navigate]);

  // Setter only navigates (URL stays the source of truth). Functional updater
  // preserves any other params (panel/focus/sub) already on the URL.
  //
  // `as never` on the options: this hook lives in the router-agnostic
  // `@discovr/ui` package, so it cannot reference the app's registered route
  // tree. In a consuming app the registered router types `navigate`'s search
  // updater strictly (tab as the enum), which fights a `string`-accepting
  // public setter (AppSidebar.onTabChange is `(tab: string) => void`). The
  // schema turns an unknown tab into `undefined`, so any string is safe at runtime.
  const setTab = useCallback(
    (next: string) =>
      navigate({
        to: '.',
        search: ((prev: Partial<CampaignSearch>) => ({
          ...prev,
          ...(prev.tab !== next ? clearedShopifyListParams() : {}),
          ...leavingDiscovery(prev, next),
          tab: next,
          pill: undefined,
          page: undefined,
        })),
      } as never),
    [navigate],
  );

  return { tab, setTab };
}
